import { Button, Paper, SxProps, Theme, Modal, AlertColor, Box, } from '@mui/material';
import { API_DOMAIN, NON_EXISTING, PERMISSIONS, POST } from '../../../utility/constants';
import { CalcIneligibleContext } from '../../../context/calneligibleContext';
import { setCalcIneligibleLoader } from '../../../actions/calcIneligibleActions';
import styles from './styles';
import { Dispatch, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import CalcIneligibleDialog from '../../../components/dialog-select'; // for upc (temp)
import CalcDialogSelect from './dialog-select'; // for Independent with AR and INV Group selection
import { calculateIneligibles, checkUserPermissions, getIneligibleSettings, getLocalStorageItem, subscribeToProcessStatusManager } from '../../../utility/helper';
import Toaster from '../../../components/toaster';
import { SelectedClientContext } from '../../../context/selectedClientContext';
import { IClient, ICollateral } from '../../../interfaces';
import { IIneligibleSettingAPI } from '../../../interfaces/ineligibleSettingInterface';
import { getInvIneligibleSettings, getUPCIneligibleSettings } from '../../../api/ineligible-settings';
import { useNavigate } from 'react-router-dom';
import { SelectedChildClientMessageModal } from '../../../components/ineligible-settings/modals/selected-child-client-message';
import axiosInstance from '../../../service/axiosInstance';
import { CalcIneligibleState } from '../../../reducer/calcIneligibleReducer';

interface ICalculateIneligibleProps {
  disabled?             : boolean;
  calculateButtonStyles?: SxProps<Theme>;
  hideFileImportLoader? : () => void;
  setParamsForCalculateUPC?: Dispatch<React.SetStateAction<{
    sessionUuid: string;
    selectedClient: IClient;
    selectedEndDate: string;
  }>>
}

/**
 * A component for calculate ineligibles button.
 * @param props The props for configuring calculate ineligibles button.
 * @returns A React component that renders calculate ineligibles button.
 */
const CalculateIneligiblesButton: FC<ICalculateIneligibleProps> = (props) => {
  const navigate                                                            = useNavigate();
  const controller                                                          = new AbortController();
  const { selectedClient, clients, setSelectedClient }                      = useContext(SelectedClientContext);
  const { state: stateForCalcIneligible, dispatch }                         = useContext(CalcIneligibleContext)
  const stateForCalcIneligibleRef                                           = useRef(stateForCalcIneligible);
  const [open, setOpen]                                                     = useState<boolean>(false);
  const [toasterOpen, setToasterOpen]                                       = useState<boolean>(false);
  const [toasterMessage, setToasterMessage]                                 = useState<string>('');
  const [toasterSeverity, setToasterSeverity]                               = useState<AlertColor>('success');
  const [childModalOpen, setChildModalOpen]                                 = useState(false);
  const isUltimateParent                                                    = useMemo(() => Boolean(selectedClient?.parentClient), [selectedClient]);
  const isChildClient                                                       = useMemo(() => Boolean(selectedClient?.parentClientFk), [selectedClient]);
  const [sessionUuid]                                                       = useState(crypto.randomUUID());

  useEffect(() => {
    stateForCalcIneligibleRef.current = stateForCalcIneligible;
  }, [stateForCalcIneligible]);

  const parent = useMemo(() => {
    if (isChildClient) {
      const parent = clients.find(client => client.recordId === selectedClient?.parentClientFk);
      if (parent) return parent;
    }
  }, [selectedClient, clients]);

  /**
   * This callback function handles opening the modal.
   */
  const handleOpen = () => {
    if (isChildClient) {
      setChildModalOpen(true);
    } else {
      setOpen(true);
    }
  };

  /**
   * This callback function handles closing the modal.
   */
  const handleClose = () => {
    if (isChildClient) {
      setChildModalOpen(false);
    } else {
      setOpen(false);
    }
  };
  /**
   * This function attempts to calculate an ineligible report.
   *
   * @param borrowerName The name of the borrower.
   * @param arCollateralName The name of the AR collateral.
   * @param borrowerId The ID of the borrower.
   * @param bbPeriodId -he ID of the BB period.
   * @param arCollateralId The ID of the AR collateral.
   */
  const tryCalculate = useCallback(async (borrowerName: string, collateralName: string, borrowerId: number, bbPeriodId: number, collateralId: number, collateralType?: string) => {
    const canCalculateIneligible = await checkUserPermissions(getLocalStorageItem('uid'), PERMISSIONS.CALCULATE_INELIGIBLE);
    if (canCalculateIneligible) {
      await calculate(borrowerName, collateralName, borrowerId, bbPeriodId, collateralId, collateralType);
      return;
    }
    setToasterOpen(true);
    setToasterMessage('You do not have the necessary permissions for this action.');
    setToasterSeverity('error');
    setOpen(false);
  }, [controller])

  const calculateInvIneligible = async (borrowerId: number, bbPeriodId: number, invCollateralId: number) => {
    await axiosInstance.request({
      url: `${API_DOMAIN}/inv/calculateIneligibles/ineligibleSettings`,
      method: POST,
      params: {
        sessionUuid,
        borrowerId,
        bbPeriodId,
        invCollateralId,
      }
    })
  }

  /**
   * This function initiates the calculation of an ineligible report.
   *
   * @param borrowerName The name of the borrower.
   * @param collateralName The name of the collateral.
   * @param borrowerId The ID of the borrower.
   * @param bbPeriodId The ID of the BB period.
   * @param collateralId The ID of the collateral.
   */
  const calculate = async (borrowerName: string, collateralName: string, borrowerId: number, bbPeriodId: number, collateralId: number, collateralType?: string) => {
    const ineligibleSettings = (
      collateralType === 'INV'
        ? await getInvIneligibleSettings({recordId: borrowerId} as Required<IClient>, {recordId: collateralId} as Required<ICollateral>)
        : (await getIneligibleSettings(borrowerId, collateralId)).data
    ) as IIneligibleSettingAPI[];

    const link = collateralType === 'INV'
      ? `/reports/inventory?clientId=${borrowerId}&bbPeriodId=${bbPeriodId}&invCollateralId=${collateralId}&currencyId=${selectedClient?.currencyId}&fromSettings=true`
      : `/reports/ar-ineligible?clientId=${borrowerId}&bbPeriodId=${bbPeriodId}&arCollateralId=${collateralId}&fromSettings=true`
    const payload: CalcIneligibleState = {
      title: 'Calculating Ineligible Report',
      content: `Ineligible Report - ${borrowerName} - ${collateralName}`,
      hasInitiatedSubscription: false,
      isLoading: true,
      show: true,
      link,
      cancelled: false,
      failed: false,
      selectedClientId: borrowerId,
      selectedCollateralId: collateralId,
      isParentClientLevel: false,
      type: collateralType === 'AR' ? 'AR' : 'Inventory',
      ineligibleSettings: ineligibleSettings,
    };
    dispatch(setCalcIneligibleLoader({...payload}));
    subscribeToProcessStatusManager({
      sessionUuid: sessionUuid,
      type: "ineligible-calculation",
      level: collateralType === "AR" ? "ar-collateral" : "inv-collateral",
      recordId: collateralId,
      onQueue: (queuedReason) => {
        dispatch(setCalcIneligibleLoader({ ...stateForCalcIneligibleRef.current, queuedReason: queuedReason }));
      },
      onRun: () => {
        dispatch(setCalcIneligibleLoader({ ...stateForCalcIneligibleRef.current, queuedReason: undefined }));
      }
    });
    setOpen(false);
    try {
      collateralType === 'INV'
        ? await calculateInvIneligible(borrowerId, bbPeriodId, collateralId)
        : await calculateIneligibles(sessionUuid, borrowerId, bbPeriodId, collateralId);

      dispatch(setCalcIneligibleLoader({ ...payload, title: 'Calculation Finished', isLoading: false }));
    } catch (error) {
      dispatch(setCalcIneligibleLoader({ ...payload,  isLoading: false, failed: true, link: '' }));
      console.log('CALCULATE ERROR: ', error);
    }
  };

  /**
   * This function initiates the calculation of the UPC ineligible report.
   * @param selectedClient The selected client to be calculated (Ultimate Parent Client).
   * @param selectedEndDate The selected end date in YYYYMMDD format.
   */
  const initiateCalculationForUPC = async (selectedClient: IClient, selectedEndDate: string) => {
    const nonNullableSelectedClient = selectedClient as NonNullable<IClient> & Required<IClient>;
    const ineligibleSettings = await getUPCIneligibleSettings(nonNullableSelectedClient);
    const initiatorPayLoad: CalcIneligibleState = { /* to initiate subscription to stomp client, before actual loading starts */
      title: 'Calculating Ineligible Report',
      content: `Ineligible Report - ${nonNullableSelectedClient.borrowerName}`,
      hasInitiatedSubscription: false,
      isLoading: true,
      show: true,
      link: `/reports/ar-ineligible?clientId=${nonNullableSelectedClient.recordId}&endDate=${selectedEndDate}`,
      cancelled: false,
      failed: false,
      selectedClientId: nonNullableSelectedClient.recordId,
      selectedCollateralId: NON_EXISTING,
      isParentClientLevel: true,
      type: 'AR',
      ineligibleSettings: ineligibleSettings,
    };
    dispatch(setCalcIneligibleLoader({ ...initiatorPayLoad }));
    props.setParamsForCalculateUPC?.({ sessionUuid, selectedClient, selectedEndDate });
    setOpen(false);
  };

  return (
    <>
      <Toaster
        open={toasterOpen}
        message={toasterMessage}
        severity={toasterSeverity}
        onCloseChange={() => setToasterOpen(false)}
      />
      <Paper
        elevation={props.calculateButtonStyles === undefined ? 3 : 0}
        sx={props.calculateButtonStyles === undefined ? styles.card : null}
      >
        <Button
          fullWidth
          onClick={handleOpen}
          sx={props.calculateButtonStyles === undefined ? styles.cardButton : props.calculateButtonStyles}
          aria-label='Calculate Ineligibles button' disabled={props.disabled}
        >
          Calculate Ineligibles
        </Button>
      </Paper>
      <Modal open={open} onClose={handleClose}>
        <Box>
        {isUltimateParent ? 
          <CalcIneligibleDialog
            handleClose={handleClose}
            title='Calculate Ineligible'
            selectBBPeriod
            forCalculate
            calculateUPC={initiateCalculationForUPC}
            hideFileImportLoader={props.hideFileImportLoader}
          />
        :
          <CalcDialogSelect
            handleClose={handleClose}
            title='Calculate Ineligible'
            selectARCollateral
            selectBBPeriod
            forCalculate
            calculateBB={tryCalculate}
            hideFileImportLoader={props.hideFileImportLoader}
          />
        } 
        </Box>
      </Modal>
      <SelectedChildClientMessageModal
        isOpen={childModalOpen}
        parentName={parent?.borrowerName ?? ''}
        handleNavigateToClientsPage={() => {
          handleClose();
          navigate('/clients');
          props.hideFileImportLoader?.();
        }}
        handleClose={() => {
          setSelectedClient(parent ?? null);
          handleClose();
          setOpen(true);
        }}
        handleButtonClose={() => {
          handleClose();
        }}
        headingTitle='Calculate Ineligibles'
        firstMessage='Calculation of ineligibles are only applicable to the Parent Client level.'
        secondMessage={`Click proceed to switch the selected Client's Parent if you want to calculate ineligibles.`}
      />
    </>
  );
};

export default CalculateIneligiblesButton;
